/**
 * Cache Manager - Utilitário simplificado para gerenciamento de cache
 */

class CacheManager {
  constructor() {
    this.isServiceWorkerSupported = 'serviceWorker' in navigator;
    this.isOnline = navigator.onLine;
    this.isProduction = process.env.NODE_ENV === 'production';
    
    if (this.isServiceWorkerSupported) {
      this.setupEventListeners();
    }
  }

  setupEventListeners() {
    navigator.serviceWorker.addEventListener('message', this.handleSWMessage.bind(this));
    
    window.addEventListener('online', () => {
      this.isOnline = true;
      if (this.isProduction) {
        this.syncCache();
      }
    });
    
    window.addEventListener('offline', () => {
      this.isOnline = false;
    });
  }

  handleSWMessage(event) {
    const { data } = event;
    // Handle SW messages if needed
  }

  isServiceWorkerActive() {
    return this.isServiceWorkerSupported && navigator.serviceWorker.controller;
  }

  async sendMessageToSW(message) {
    if (!this.isServiceWorkerActive()) {
      throw new Error('Service Worker não está ativo');
    }

    return new Promise((resolve, reject) => {
      const channel = new MessageChannel();
      
      channel.port1.onmessage = (event) => {
        if (event.data.error) {
          reject(new Error(event.data.error));
        } else {
          resolve(event.data);
        }
      };

      navigator.serviceWorker.controller.postMessage(message, [channel.port2]);
    });
  }

  async getCacheStats() {
    if (!this.isProduction) return {};
    
    try {
      const response = await this.sendMessageToSW({ type: 'CHECK_VERSION' });
      return response.cacheStats || {};
    } catch (error) {
      return {};
    }
  }

  async warmCache(urls = []) {
    if (!this.isProduction || !Array.isArray(urls) || urls.length === 0) {
      return false;
    }

    try {
      await this.sendMessageToSW({
        type: 'WARM_CACHE',
        data: { urls }
      });
      return true;
    } catch (error) {
      return false;
    }
  }

  async clearCache(cacheName = null) {
    try {
      if (cacheName) {
        await this.sendMessageToSW({
          type: 'CLEAR_SPECIFIC_CACHE',
          data: { cacheName }
        });
      } else {
        await this.sendMessageToSW({ type: 'FORCE_UPDATE' });
      }
      return true;
    } catch (error) {
      return false;
    }
  }

  async forceUpdate() {
    try {
      await this.sendMessageToSW({ type: 'FORCE_UPDATE' });
      return true;
    } catch (error) {
      return false;
    }
  }

  async syncCache() {
    if (!this.isOnline || !this.isProduction) {
      return false;
    }

    try {
      const criticalUrls = [
        '/',
        '/manifest.json',
      ];

      await this.warmCache(criticalUrls);
      return true;
    } catch (error) {
      return false;
    }
  }

  async optimizeCache() {
    if (!this.isProduction) return [];
    
    try {
      const stats = await this.getCacheStats();
      const optimizations = [];

      Object.entries(stats).forEach(([cacheName, itemCount]) => {
        if (itemCount > 100) {
          optimizations.push({
            cache: cacheName,
            action: 'cleanup',
            reason: `Muitos itens (${itemCount})`
          });
        }
      });

      return optimizations;
    } catch (error) {
      return [];
    }
  }

  startPerformanceMonitoring() {
    if (!this.isProduction || !('PerformanceObserver' in window)) {
      return;
    }

    const observer = new PerformanceObserver((list) => {
      // Performance monitoring logic here if needed
    });

    observer.observe({ entryTypes: ['navigation', 'resource'] });
  }

  async generateCacheReport() {
    const stats = await this.getCacheStats();
    const totalItems = Object.values(stats).reduce((sum, count) => sum + count, 0);
    
    return {
      timestamp: new Date().toISOString(),
      isOnline: this.isOnline,
      serviceWorkerActive: this.isServiceWorkerActive(),
      totalCaches: Object.keys(stats).length,
      totalItems: totalItems,
      cacheDetails: stats,
    };
  }

  async preloadCriticalResources() {
    if (!this.isProduction) return false;
    
    const criticalResources = [
      '/',
      '/manifest.json',
    ];

    try {
      await this.warmCache(criticalResources);
      return true;
    } catch (error) {
      return false;
    }
  }
}

// Singleton instance
const cacheManager = new CacheManager();

// Métodos de conveniência para uso em componentes Vue
export const useCacheManager = () => {
  return {
    getCacheStats: () => cacheManager.getCacheStats(),
    clearCache: (cacheName) => cacheManager.clearCache(cacheName),
    warmCache: (urls) => cacheManager.warmCache(urls),
    forceUpdate: () => cacheManager.forceUpdate(),
    generateReport: () => cacheManager.generateCacheReport(),
    optimize: () => cacheManager.optimizeCache(),
    preloadCritical: () => cacheManager.preloadCriticalResources(),
    isOnline: () => cacheManager.isOnline,
    isActive: () => cacheManager.isServiceWorkerActive()
  };
};

export default cacheManager; 